import { useQuery } from '@tanstack/react-query'
import { Server, Network, UserPlus, Wifi } from 'lucide-react'
import { statsApi } from '../api/client'
import type { DashboardStats } from '../types'

interface StatCardProps {
  title: string
  value: number
  icon: React.ReactNode
  color: string
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: statsApi.getDashboard,
    refetchInterval: 30000,
  })

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 w-48 bg-slate-700 rounded mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-800 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Devices"
          value={stats?.devices.total ?? 0}
          icon={<Server className="w-6 h-6 text-blue-400" />}
          color="bg-blue-500/10"
        />
        <StatCard
          title="Active Devices"
          value={stats?.devices.active ?? 0}
          icon={<Server className="w-6 h-6 text-green-400" />}
          color="bg-green-500/10"
        />
        <StatCard
          title="Connected Now"
          value={stats?.devices.connected ?? 0}
          icon={<Wifi className="w-6 h-6 text-purple-400" />}
          color="bg-purple-500/10"
        />
        <StatCard
          title="Total Tunnels"
          value={stats?.tunnels.total ?? 0}
          icon={<Network className="w-6 h-6 text-cyan-400" />}
          color="bg-cyan-500/10"
        />
      </div>

      {(stats?.enrollments.pending ?? 0) > 0 && (
        <div className="mt-8">
          <div className="card bg-yellow-500/10 border-yellow-500/50">
            <div className="flex items-center gap-3">
              <UserPlus className="w-6 h-6 text-yellow-400" />
              <div>
                <p className="text-yellow-400 font-medium">
                  {stats!.enrollments.pending} device(s) pending approval
                </p>
                <p className="text-slate-400 text-sm">
                  Go to Enroll to approve new devices
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <a
              href="/enroll"
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors"
            >
              <UserPlus className="w-5 h-5 text-primary-400" />
              <span className="text-slate-200">Approve pending devices</span>
            </a>
            <a
              href="/devices"
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors"
            >
              <Server className="w-5 h-5 text-primary-400" />
              <span className="text-slate-200">Manage devices</span>
            </a>
            <a
              href="/tunnels"
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors"
            >
              <Network className="w-5 h-5 text-primary-400" />
              <span className="text-slate-200">View all tunnels</span>
            </a>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Getting Started</h2>
          <div className="space-y-4 text-slate-300 text-sm">
            <p>
              <strong className="text-white">1. Install the agent</strong>
              <br />
              Run the installer on your edge device:
              <code className="block mt-2 p-2 bg-slate-700 rounded text-xs">
                curl -fsSL https://get.gatewarden.net | sudo bash
              </code>
            </p>
            <p>
              <strong className="text-white">2. Approve the device</strong>
              <br />
              Enter the enrollment code shown on the device in the Enroll page.
            </p>
            <p>
              <strong className="text-white">3. Create tunnels</strong>
              <br />
              Configure tunnels to expose services from your edge device.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
